import PropTypes from "prop-types";

/**
 * GradientSeparator - A reusable gradient separator component
 * Creates a horizontal line with a gradient fade effect on both ends
 * 
 * @param {string} color - The color for the gradient (default: "#1A2127" - dark)
 * @param {string} variant - Color variant: "dark" or "white" (default: "dark")
 * @param {string|number} marginTop - Top margin (default: "16px")
 * @param {string|number} marginBottom - Bottom margin (default: "16px")
 * @param {string} width - Width of the separator (default: "100%")
 * @param {string} height - Height of the separator (default: "1px")
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
const GradientSeparator = ({
  color,
  variant = "dark",
  marginTop = "16px",
  marginBottom = "16px",
  width = "100%",
  height = "1px",
  className = "",
  style = {},
}) => {
  // Determine the color based on variant or prop
  let gradientColor;
  if (color) {
    gradientColor = color;
  } else if (variant === "white") {
    gradientColor = "rgba(255, 255, 255, 1)";
  } else {
    gradientColor = "rgba(26, 33, 39, 1)"; // Default dark
  }

  // Extract RGB values from color string for gradient
  // Handle rgba, rgb, hex, and named colors
  const getGradientColor = (colorStr) => {
    if (colorStr.startsWith("rgba")) {
      return colorStr;
    } else if (colorStr.startsWith("rgb")) {
      return colorStr.replace("rgb", "rgba").replace(")", ", 1)");
    } else if (colorStr.startsWith("#")) {
      // Convert hex to rgba
      const hex = colorStr.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 1)`;
    }
    return colorStr; // Fallback for named colors
  };

  const fullColor = getGradientColor(gradientColor);
  const transparentColor = fullColor.replace(/,\s*[\d.]+\)$/, ", 0)");

  const separatorStyle = {
    width,
    height,
    background: `linear-gradient(to right, ${transparentColor}, ${fullColor} 20%, ${fullColor} 80%, ${transparentColor})`,
    marginTop,
    marginBottom,
    ...style,
  };

  return <div className={`gradient-separator ${className}`} style={separatorStyle} />;
};

GradientSeparator.propTypes = {
  color: PropTypes.string,
  variant: PropTypes.oneOf(["dark", "white"]),
  marginTop: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.string,
  height: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default GradientSeparator;

